import { useCallback, useEffect, useState } from "react";

const AUTH_API_URL = import.meta.env.VITE_AUTH_API_URL || "";
const JWT_KEY = "nightswarm_jwt";

export interface AuthUser {
  email: string;
  ownedEntitlements: string[];
  createdAt: number | null;
  lastLoginAt: number | null;
}

export type AuthStatus = "loading" | "signed-out" | "signed-in";

// iOS standalone PWAs don't share localStorage with Safari, but cookies do.
// We mirror the JWT into both so the installed PWA picks up sessions.
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, days: number) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax; Secure`;
}

function deleteCookie(name: string) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax; Secure`;
}

function getStoredJwt(): string | null {
  try {
    return localStorage.getItem(JWT_KEY) || getCookie(JWT_KEY);
  } catch {
    return null;
  }
}

function setStoredJwt(jwt: string): void {
  try {
    localStorage.setItem(JWT_KEY, jwt);
    setCookie(JWT_KEY, jwt, 30);
  } catch {}
}

function clearStoredJwt(): void {
  try {
    localStorage.removeItem(JWT_KEY);
    deleteCookie(JWT_KEY);
  } catch {}
}

// ─── Return-to across the magic-link round-trip ─────────────────────
// /auth/verify always redirects to `${SITE_URL}/?jwt=...` — the ROOT. The
// path the user was on (and its query params) does NOT survive the email
// hop server-side. Pages that need their params back after sign-in
// (/enroll/desktop's `port`/`state` — tauri-desktop.md "One-click
// enrollment" §2) stash a same-origin path here before requesting the
// link; boot() consumes the stash right after a jwt-link sign-in.
// localStorage (not sessionStorage) on purpose: the link often opens in a
// NEW tab, and sessionStorage doesn't cross tabs.
const RETURN_TO_KEY = "nightswarm_return_to";
// Matches the magic-link token TTL — a stale stash must not hijack an
// unrelated sign-in days later.
const RETURN_TO_TTL_MS = 15 * 60 * 1000;

/** Stash a same-origin path (+query) to land on after the magic-link
 *  round-trip. Rejects anything that isn't a local absolute path ("//host"
 *  would be an open redirect). */
export function setReturnTo(path: string): void {
  try {
    if (!path.startsWith("/") || path.startsWith("//")) return;
    localStorage.setItem(
      RETURN_TO_KEY,
      JSON.stringify({ path, ts: Date.now() })
    );
  } catch {}
}

export function clearReturnTo(): void {
  try {
    localStorage.removeItem(RETURN_TO_KEY);
  } catch {}
}

/** Pop the stashed path — one-shot, TTL-checked, same-origin-validated. */
function consumeReturnTo(): string | null {
  try {
    const raw = localStorage.getItem(RETURN_TO_KEY);
    if (!raw) return null;
    localStorage.removeItem(RETURN_TO_KEY);
    const parsed = JSON.parse(raw) as { path?: string; ts?: number };
    if (
      typeof parsed.path !== "string" ||
      !parsed.path.startsWith("/") ||
      parsed.path.startsWith("//")
    ) {
      return null;
    }
    if (
      typeof parsed.ts !== "number" ||
      Date.now() - parsed.ts > RETURN_TO_TTL_MS
    ) {
      return null;
    }
    return parsed.path;
  } catch {
    return null;
  }
}

/** Pull `?jwt=...` off the magic-link landing URL, then scrub it from history. */
function extractJwtFromLocation(): string | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const jwt = params.get("jwt");
    if (jwt && jwt.length > 0) {
      params.delete("jwt");
      const qs = params.toString();
      const clean = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`;
      window.history.replaceState(null, "", clean);
      return jwt;
    }
    return null;
  } catch {
    return null;
  }
}

export function useAuth() {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [jwt, setJwt] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchMe = useCallback(async (token: string): Promise<AuthUser | null> => {
    try {
      const res = await fetch(`${AUTH_API_URL}/auth/me`, {
        method: "GET",
        headers: { authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      return (await res.json()) as AuthUser;
    } catch {
      return null;
    }
  }, []);

  const acceptJwt = useCallback(
    async (token: string): Promise<boolean> => {
      const u = await fetchMe(token);
      if (!u) {
        clearStoredJwt();
        setJwt(null);
        setUser(null);
        setStatus("signed-out");
        return false;
      }
      setStoredJwt(token);
      setJwt(token);
      setUser(u);
      setStatus("signed-in");
      // Mirror into the HttpOnly SSO cookie (best-effort): keeps
      // /oauth/authorize auto-approving and lets a freshly installed PWA
      // (separate localStorage, shared cookies) bootstrap via GET.
      fetch(`${AUTH_API_URL}/auth/session`, {
        method: "POST",
        headers: { authorization: `Bearer ${token}` },
        credentials: "include",
      }).catch(() => {});
      return true;
    },
    [fetchMe]
  );

  // PWA bootstrap: no JWT in this storage partition, but the same-origin
  // HttpOnly SSO cookie may ride along — exchange it for a fresh JWT.
  const bootstrapFromCookie = useCallback(async (): Promise<string | null> => {
    try {
      const res = await fetch(`${AUTH_API_URL}/auth/session`, {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) return null;
      const body = (await res.json()) as { jwt?: string };
      return body.jwt && typeof body.jwt === "string" ? body.jwt : null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      const tokenFromLink = extractJwtFromLocation();
      if (tokenFromLink) {
        const ok = await acceptJwt(tokenFromLink);
        // Magic-link landing is ALWAYS "/" (verify.ts redirects to the
        // site root) — bounce to the stashed pre-sign-in page so its
        // query params (/enroll/desktop?port&state) survive the
        // round-trip. Full navigation: the JWT is already in storage, so
        // the destination boots signed-in.
        if (ok) {
          const returnTo = consumeReturnTo();
          const here = `${window.location.pathname}${window.location.search}`;
          if (returnTo && returnTo !== here) {
            window.location.replace(returnTo);
          }
        }
        return;
      }
      const stored = getStoredJwt();
      if (stored) {
        const u = await fetchMe(stored);
        if (cancelled) return;
        if (u) {
          setJwt(stored);
          setUser(u);
          setStatus("signed-in");
          return;
        }
        clearStoredJwt();
      }
      // Fresh partition (installed PWA) — try the SSO-cookie exchange
      // before giving up.
      const cookieJwt = await bootstrapFromCookie();
      if (cancelled) return;
      if (cookieJwt && (await acceptJwt(cookieJwt))) return;
      if (!cancelled) setStatus("signed-out");
    };

    boot();
    return () => {
      cancelled = true;
    };
  }, [fetchMe, acceptJwt, bootstrapFromCookie]);

  const requestMagicLink = useCallback(
    async (email: string): Promise<{ ok: boolean; error?: string }> => {
      setError(null);
      try {
        const res = await fetch(`${AUTH_API_URL}/auth/request`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ email }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const msg = body.error || "request_failed";
          setError(msg);
          return { ok: false, error: msg };
        }
        return { ok: true };
      } catch {
        setError("network_error");
        return { ok: false, error: "network_error" };
      }
    },
    []
  );

  const signOut = useCallback(() => {
    clearStoredJwt();
    setJwt(null);
    setUser(null);
    setStatus("signed-out");
  }, []);

  const deleteAccount = useCallback(async (): Promise<boolean> => {
    if (!jwt) return false;
    try {
      const res = await fetch(`${AUTH_API_URL}/auth/me`, {
        method: "DELETE",
        headers: { authorization: `Bearer ${jwt}` },
      });
      if (!res.ok) return false;
      signOut();
      return true;
    } catch {
      return false;
    }
  }, [jwt, signOut]);

  return {
    status,
    jwt,
    user,
    error,
    requestMagicLink,
    signOut,
    deleteAccount,
    acceptJwt,
  };
}
